import React, { useState, useEffect } from 'react'
import {
    Box,
    Paper,
    Text,
    Button,
    Flex,
    Divider,
    SimpleGrid,
    TextInput,
    ActionIcon,
    NumberInput,
    Table,
    Image,
    Modal,
    Tabs,
    FileInput,
    Group,
    Stack,
    Badge,
    ThemeIcon,
    Title,
    Grid,
    Card
} from '@mantine/core'
import { modals } from '@mantine/modals'
import {
    IconPlus,
    IconTrash,
    IconArrowRight,
    IconBolt,
    IconDatabase,
    IconPhoto,
    IconTags,
    IconBoxSeam,
    IconWand,
} from '@tabler/icons-react'
import { notificationError } from '../../../../component/ui/Notifications'
import NoImage from '../../../../assets/img/images/no-images.png'
import { convertFileToImageVariant } from '../../../../services/products'

const ProductVariantsSection = ({ onUpdate, currency = 'IDR', quantityUnit = 'pcs'}) => {
    const [enableVariant, setEnableVariant] = useState(false)
    const [variantsStructure, setVariantsStructure] = useState([]) // [{ option: 'Color', value: ['Red', 'Blue'] }]
    const [generatedVariants, setGeneratedVariants] = useState([]) // [{ items: [...], price: 0, ... }]
    const [isTableGenerated, setIsTableGenerated] = useState(false)

    // Image adjust modal state
    const [modalAdjustImage, setModalAdjustImage] = useState(false)
    const [selectedVariantIndex, setSelectedVariantIndex] = useState(null)
    const [tempImageUrl, setTempImageUrl] = useState('')

    // New Image Modal State
    const [selectedMethodAdjustImage, setSelectedMethodAdjustImage] = useState('url')
    const [uploadImage, setUploadImage] = useState(null)

    // Auto-fill state
    const [autoFillData, setAutoFillData] = useState({
        price: '',
        sale_price: '',
        reseller_price: '',
        quantity: '',
        ali_sku: '',
    })

    // Notify parent whenever important state changes
    useEffect(() => {
        if (!enableVariant) {
            onUpdate({ skus: [], attributes: [] })
            return
        }

        if (isTableGenerated && generatedVariants.length > 0) {
            // Prepare attributes (flattened structure for backend)
            const attributes = variantsStructure.flatMap(v =>
                v.value.map(val => ({
                    key: v.option,
                    value: val,
                    image_url: "",
                    original_key: "",
                    original_value: val,
                    property: "",
                    key_id: "",
                    // other fields will be handled by backend or are null for new
                }))
            )
            onUpdate({ skus: generatedVariants, attributes })
        } else {
            onUpdate({ skus: [], attributes: [] })
        }
    }, [enableVariant, isTableGenerated, generatedVariants, variantsStructure, onUpdate])

    // --- Helpers ---
    const handleToggleVariant = () => {
        if (enableVariant) {
            modals.openConfirmModal({
                title: 'Disable Variants',
                centered: true,
                children: (
                    <Text size="sm">
                        Are you sure you want to disable variants? This will remove all variant data.
                    </Text>
                ),
                labels: { confirm: 'Disable Variants', cancel: 'Cancel' },
                confirmProps: { color: 'red' },
                onConfirm: () => {
                    setEnableVariant(false)
                    setVariantsStructure([])
                    setGeneratedVariants([])
                    setIsTableGenerated(false)
                }
            })
        } else {
            setEnableVariant(true)
            setVariantsStructure([{ option: '', value: [''] }])
        }
    }

    const handleAddStructure = () => {
        if (variantsStructure.length >= 3) return
        setVariantsStructure([...variantsStructure, { option: '', value: [''] }])
        setIsTableGenerated(false)
    }


    const handleStructureChange = (index, field, val) => {
        const newStruct = [...variantsStructure]
        newStruct[index][field] = val
        setVariantsStructure(newStruct)
        setIsTableGenerated(false)
    }

    const handleRemoveStructure = (index) => {
        const newStruct = variantsStructure.filter((_, i) => i !== index)
        setVariantsStructure(newStruct)
        setIsTableGenerated(false)
    }

    const handleAddValue = (sIndex) => {
        const newStruct = [...variantsStructure]
        newStruct[sIndex].value.push('')
        setVariantsStructure(newStruct)
        setIsTableGenerated(false)
    }

    const handleRemoveValue = (sIndex, vIndex) => {
        const newStruct = [...variantsStructure]
        newStruct[sIndex].value = newStruct[sIndex].value.filter((_, i) => i !== vIndex)
        setVariantsStructure(newStruct)
        setIsTableGenerated(false)
    }

    const handleValueChange = (sIndex, vIndex, val) => {
        const newStruct = [...variantsStructure]
        newStruct[sIndex].value[vIndex] = val
        setVariantsStructure(newStruct)
        setIsTableGenerated(false)
    }

    const handleConvertToURL = async (val) => {
        const fileImage = val

        const formData = new FormData()
        formData.append(`image`, fileImage)
        try {
            const response = await convertFileToImageVariant(formData)
            if (response.success) {
                setUploadImage(val)
                setTempImageUrl(response.image_url)
            } else {
                notificationError('Failed To Convert Image Variant', 'Please contact your administrator')
            }
        } catch (error) {
            console.log(error)
            notificationError('Failed To Convert Image Variant', 'Please contact your administrator')
        }
    }

    // --- Generator Logic ---
    const generateCombinations = () => {
        // Validation
        for (let s of variantsStructure) {
            if (!s.option.trim()) {
                notificationError("Validaton", "Variant name cannot be empty")
                return
            }
            if (s.value.some(v => !v.trim())) {
                notificationError("Validaton", "Variant options cannot be empty")
                return
            }
        }

        // Prepare data for cartesian product
        // We want array of arrays where each sub-array is the list of items for one variant type
        // e.g. [ [{key:'Color', value:'Red'}, {key:'Color', value:'Blue'}],  [{key:'Size', value:'S'}...] ]

        const structureAsItems = variantsStructure.map(vs =>
            vs.value.map(val => ({
                key: vs.option,
                value: val,
                image_url: "",
                original_key: "",
                original_value: val,
                property: "",
                key_id: "",
            }))
        )

        const combinations = structureAsItems.reduce((a, b) => a.flatMap(x => b.map(y => [...x, y])), [[]])

        // combinations is now array of arrays of objects: [[{key:Color, val:Red}, {key:Size, val:S}], ...]

        const newVariants = combinations.map(combo => ({
            items: combo,
            _combinationIdentification: combo.map(c => `${c.key}:${c.value}`).join(';'),
            price: 0,
            sale_price: 0,
            reseller_price: 0,
            quantity: 0,
            ali_sku: '',
            image_url: '',
            sku_id: '0' // Default for new
        }))

        setGeneratedVariants(newVariants)
        setIsTableGenerated(true)
    }

    const handleVariantDataChange = (index, field, val) => {
        const newVars = [...generatedVariants]
        newVars[index][field] = val
        setGeneratedVariants(newVars)
    }

    const handleAutoFill = () => {
        const newVars = generatedVariants.map(v => ({
            ...v,
            price: autoFillData.price !== '' ? autoFillData.price : v.price,
            sale_price: autoFillData.sale_price !== '' ? autoFillData.sale_price : v.sale_price,
            reseller_price: autoFillData.reseller_price !== '' ? autoFillData.reseller_price : v.reseller_price,
            quantity: autoFillData.quantity !== '' ? autoFillData.quantity : v.quantity,
            ali_sku: autoFillData.ali_sku !== '' ? (`${autoFillData.ali_sku}`) : v.ali_sku // Smart SKU appending
        }))
        setGeneratedVariants(newVars)
    }

    // --- UI Components ---

    const Infographic = () => (
        <Paper p="xl" radius="lg" withBorder style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <Flex direction={{ base: 'column', sm: 'row' }} align="center" gap="xl">
                <Box style={{ flex: 1 }}>
                    <ThemeIcon size={60} radius="xl" color="blue" variant="light" mb="md">
                        <IconTags size={32} />
                    </ThemeIcon>
                    <Title order={3} mb="sm">Enable Product Variants</Title>
                    <Text c="dimmed" mb="xl">
                        Turn on this feature to offer multiple options like Sizes, Colors, or Materials for a single product listing.
                    </Text>

                    <SimpleGrid cols={1} spacing="sm">
                        <Group>
                            <ThemeIcon color="teal" radius="xl" size="sm"><IconArrowRight size={12} /></ThemeIcon>
                            <Text size="sm">Manage Stock Independently for each option</Text>
                        </Group>
                        <Group>
                            <ThemeIcon color="teal" radius="xl" size="sm"><IconArrowRight size={12} /></ThemeIcon>
                            <Text size="sm">Set Different Prices (Retail, Sale, Dropship)</Text>
                        </Group>
                        <Group>
                            <ThemeIcon color="teal" radius="xl" size="sm"><IconArrowRight size={12} /></ThemeIcon>
                            <Text size="sm">Assign Specific Images to each variation</Text>
                        </Group>
                    </SimpleGrid>

                    <Button onClick={handleToggleVariant} mt="xl" size="md" leftSection={<IconBolt size={18} />} color="blue">
                        Enable Variants
                    </Button>
                </Box>
                <Box style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    {/* Abstract Visual Representation */}
                    <Card shadow="sm" radius="md" withBorder w="100%" maw={300} bg="white">
                        <Stack gap="xs">
                            <Group position="apart">
                                <Box w={40} h={10} bg="gray.2" radius="xl" />
                                <Box w={20} h={10} bg="gray.2" radius="xl" />
                            </Group>
                            <Box w="100%" h={100} bg="blue.0" radius="md" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <IconBoxSeam size={40} color="#74c0fc" />
                            </Box>
                            <Group grow>
                                <Box bg="blue.1" h={30} radius="sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Text size="xs" c="blue">S</Text></Box>
                                <Box bg="gray.1" h={30} radius="sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Text size="xs" c="gray">M</Text></Box>
                                <Box bg="gray.1" h={30} radius="sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Text size="xs" c="gray">L</Text></Box>
                            </Group>
                        </Stack>
                    </Card>
                </Box>
            </Flex>
        </Paper>
    )

    return (
        <Box>
            {!enableVariant ? <Infographic /> : (
                <Paper p="xl" withBorder radius="lg" shadow="sm">
                    <Flex justify="space-between" align="center" mb="lg">
                        <Box>
                            <Text fw={600} size="lg">Product Variants</Text>
                            <Text size="sm" c="dimmed">Define options and set specific prices/stocks.</Text>
                        </Box>
                        <Button variant="subtle" color="red" size="xs" onClick={handleToggleVariant}>Disable Variants</Button>
                    </Flex>

                    <Grid gutter={40}>
                        {/* Step 1: Structure */}
                        <Grid.Col span={{ base: 12, md: isTableGenerated ? 10 : 8 }}>
                            <Stack>
                                <Group position="apart">
                                    <Text fw={600} size="md">1. Define Variant Types</Text>
                                    {!isTableGenerated && (
                                        <Button size="xs" onClick={generateCombinations} leftSection={<IconDatabase size={14} />}>
                                            Generate Data
                                        </Button>
                                    )}
                                    {isTableGenerated && (
                                        <Button size="xs" variant="light" onClick={() => setIsTableGenerated(false)}>
                                            Edit Structure
                                        </Button>
                                    )}
                                </Group>

                                <Box p="md" style={{ borderRadius: '8px', border: '1px solid #eee', backgroundColor: isTableGenerated ? '#f8f9fa' : 'white' }}>
                                    {variantsStructure.map((struct, sIndex) => (
                                        <Box key={sIndex} mb="lg" style={{ opacity: isTableGenerated ? 0.6 : 1, pointerEvents: isTableGenerated ? 'none' : 'auto' }}>
                                            <Grid align="center">
                                                <Grid.Col span={6}>
                                                    <TextInput
                                                        label={sIndex === 0 ? "Variant Name" : null}
                                                        placeholder="e.g. Color"
                                                        value={struct.option}
                                                        onChange={(e) => handleStructureChange(sIndex, 'option', e.currentTarget.value)}
                                                        rightSection={
                                                            <ActionIcon size="xs" color="red" variant="transparent" onClick={() => handleRemoveStructure(sIndex)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        }
                                                    />
                                                </Grid.Col>
                                                <Grid.Col span={6}>
                                                    <Text size="sm" fw={500} mb={3}>{sIndex === 0 ? "Option Values" : ""}</Text>
                                                    <Flex wrap="wrap" gap="xs">
                                                        {struct.value.map((val, vIndex) => (
                                                            <TextInput
                                                                key={vIndex}
                                                                placeholder="Value"
                                                                value={val}
                                                                onChange={(e) => handleValueChange(sIndex, vIndex, e.currentTarget.value)}
                                                                style={{ width: '250px' }}
                                                                rightSection={
                                                                    struct.value.length > 1 && (
                                                                        <ActionIcon size="xs" color="red" variant="transparent" onClick={() => handleRemoveValue(sIndex, vIndex)}>
                                                                            <IconTrash size={10} />
                                                                        </ActionIcon>
                                                                    )
                                                                }
                                                            />
                                                        ))}
                                                        <Button variant="light" size="xs" p={0} w={30} onClick={() => handleAddValue(sIndex)}><IconPlus size={14} /></Button>
                                                    </Flex>
                                                </Grid.Col>
                                            </Grid>
                                            {sIndex < variantsStructure.length - 1 && <Divider my="sm" variant="dashed" />}
                                        </Box>
                                    ))}

                                    {!isTableGenerated && variantsStructure.length < 3 && (
                                        <Button variant="outline" fullWidth style={{ borderStyle: 'dashed' }} leftSection={<IconPlus size={14} />} onClick={handleAddStructure}>
                                            Add Another Option Type
                                        </Button>
                                    )}
                                </Box>
                            </Stack>
                        </Grid.Col>

                        {/* Step 2: Data Table */}
                        {isTableGenerated && (
                            <Grid.Col span={12}>
                                <Divider mb="lg" />
                                <Text fw={600} size="md" mb="md">2. Manage Variant Data</Text>

                                {/* Auto Fill Toolbar */}
                                <Paper p="md" bg="gray.0" mb="md">
                                    <Flex align="flex-end" gap="md" wrap="wrap">
                                        <Text size="sm" fw={600} mb={-5} w="100%"><IconWand size={14} style={{ marginRight: 4 }} /> Auto-Fill Values</Text>
                                        <NumberInput
                                            label="Retail"
                                            placeholder="0"
                                            hideControls
                                            leftSection={<Text size="xs" c="dimmed">{currency}</Text>}
                                            thousandSeparator="."
                                            decimalSeparator=','
                                            size="xs"
                                            w={150}
                                            value={autoFillData.price}
                                            onChange={(v) => setAutoFillData({ ...autoFillData, price: v })}
                                        />
                                        <NumberInput
                                            label="Sale"
                                            thousandSeparator="."
                                            decimalSeparator=','
                                            hideControls
                                            leftSection={<Text size="xs" c="dimmed">{currency}</Text>}
                                            placeholder="0"
                                            size="xs"
                                            w={150}
                                            value={autoFillData.sale_price}
                                            onChange={(v) => setAutoFillData({ ...autoFillData, sale_price: v })}
                                        />
                                        <NumberInput
                                            label="Dropship"
                                            thousandSeparator="."
                                            decimalSeparator=','
                                            hideControls
                                            leftSection={<Text size="xs" c="dimmed">{currency}</Text>}
                                            placeholder="0"
                                            size="xs"
                                            w={150}
                                            value={autoFillData.reseller_price}
                                            onChange={(v) => setAutoFillData({ ...autoFillData, reseller_price: v })}
                                        />
                                        <NumberInput
                                            label="Qty"
                                            placeholder="0"
                                            hideControls
                                            rightSection={<Text size="xs" c="dimmed" mr="xs">{quantityUnit}</Text>}
                                            size="xs"
                                            w={100}
                                            value={autoFillData.quantity}
                                            onChange={(v) => setAutoFillData({ ...autoFillData, quantity: v })}
                                        />
                                        <TextInput
                                            label="SKU Prefix"
                                            placeholder="ALI-"
                                            size="xs"
                                            w={170}
                                            value={autoFillData.ali_sku}
                                            onChange={(e) => setAutoFillData({ ...autoFillData, ali_sku: e.currentTarget.value })}
                                        />
                                        <Button size="xs" color="teal" onClick={handleAutoFill}>Apply to All</Button>
                                    </Flex>
                                </Paper>

                                <Table.ScrollContainer minWidth={800}>
                                    <Table striped highlightOnHover withTableBorder>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Variant</Table.Th>
                                                <Table.Th width={130}>Retail Price</Table.Th>
                                                <Table.Th width={130}>Sale Price</Table.Th>
                                                <Table.Th width={130}>Dropship Price</Table.Th>
                                                <Table.Th width={100}>Stock</Table.Th>
                                                <Table.Th width={150}>SKU</Table.Th>
                                                <Table.Th width={60}>Image</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {generatedVariants.map((item, index) => (
                                                <Table.Tr key={index}>
                                                    <Table.Td>
                                                        <Stack gap={2}>
                                                            {item.items.map((i, idx) => (
                                                                <Badge key={idx} size="sm" variant="dot" color="gray">{i.key}: {i.value}</Badge>
                                                            ))}
                                                        </Stack>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <NumberInput
                                                            size="xs" hideControls
                                                            value={item.price}
                                                            leftSection={<Text size="xs" c="dimmed">{currency}</Text>}
                                                            thousandSeparator="."
                                                            decimalSeparator=','
                                                            onChange={(v) => handleVariantDataChange(index, 'price', v)}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <NumberInput
                                                            size="xs" hideControls
                                                            value={item.sale_price}
                                                            leftSection={<Text size="xs" c="dimmed">{currency}</Text>}
                                                            thousandSeparator="."
                                                            decimalSeparator=','
                                                            onChange={(v) => handleVariantDataChange(index, 'sale_price', v)}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <NumberInput
                                                            size="xs" hideControls
                                                            value={item.reseller_price}
                                                            leftSection={<Text size="xs" c="dimmed">{currency}</Text>}
                                                            thousandSeparator="."
                                                            decimalSeparator=','
                                                            onChange={(v) => handleVariantDataChange(index, 'reseller_price', v)}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <NumberInput
                                                            size="xs" hideControls
                                                            rightSection={<Text size="xs" c="dimmed" mr="xs">{quantityUnit}</Text>}
                                                            value={item.quantity}
                                                            onChange={(v) => handleVariantDataChange(index, 'quantity', v)}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <TextInput
                                                            size="xs"
                                                            value={item.ali_sku}
                                                            onChange={(e) => handleVariantDataChange(index, 'ali_sku', e.currentTarget.value)}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <ActionIcon variant="default" onClick={() => {
                                                            setSelectedVariantIndex(index)
                                                            setTempImageUrl(item.image_url)
                                                            setModalAdjustImage(true)
                                                        }}>
                                                            {item.image_url ? <Image src={item.image_url} w={20} h={20} /> : <IconPhoto size={16} />}
                                                        </ActionIcon>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </Table.ScrollContainer>
                            </Grid.Col>
                        )}
                    </Grid>
                </Paper>
            )}

            <Modal
                opened={modalAdjustImage}
                onClose={() => setModalAdjustImage(false)}
                title={<Text fw="bold">Set Variant Image</Text>}
                size="lg"
                centered
            >
                <Box>
                    <Tabs
                        size='xs'
                        my={4}
                        variant="pills"
                        radius="xl"
                        value={selectedMethodAdjustImage}
                        onChange={(val) => [setSelectedMethodAdjustImage(val), setTempImageUrl(''), setUploadImage(null)]}>
                        <Tabs.List>
                            <Tabs.Tab value="url">From URL</Tabs.Tab>
                            <Tabs.Tab value="upload">From Local Computer</Tabs.Tab>
                        </Tabs.List>
                        <Tabs.Panel value="url">
                            <Box my={12}>
                                <TextInput
                                    label="Image URL"
                                    value={tempImageUrl}
                                    onChange={(val) => setTempImageUrl(val.target.value)}
                                    withAsterisk
                                    size='xs'
                                    placeholder="https://..."
                                />
                            </Box>
                        </Tabs.Panel>
                        <Tabs.Panel value="upload">
                            <Box my={12}>
                                <FileInput
                                    label="Upload Image"
                                    value={uploadImage}
                                    onChange={(val) => handleConvertToURL(val)}
                                    accept="image/jpeg,image/jpg,image/png"
                                    clearable
                                    withAsterisk
                                    size='xs'
                                />
                            </Box>
                        </Tabs.Panel>
                    </Tabs>
                    <Box>
                        <Text fz='md' fw='500' mt={10}>Preview Image</Text>
                        <Box my={20}>
                            <Flex gap="xl" justify="center" align="center">
                                <Paper shadow="sm" w={182} withBorder radius='md' p={2}>
                                    <Box bg="gray.1" style={{ width: 180, height: 180, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Image w={180} h={180} radius='md' src={tempImageUrl || NoImage} fit="contain" fallbackSrc={NoImage} />
                                    </Box>
                                    <Text size="xs" ta="center" mt={5}>New Image</Text>
                                </Paper>
                            </Flex>
                        </Box>
                    </Box>
                    <Box mt={20}>
                        <Flex justify='flex-end'>
                            <Group>
                                <Button size='xs' variant="outline" onClick={() => setModalAdjustImage(false)}>Close</Button>
                                <Button size='xs' variant="filled" disabled={!tempImageUrl} onClick={() => {
                                    handleVariantDataChange(selectedVariantIndex, 'image_url', tempImageUrl)
                                    setModalAdjustImage(false)
                                }}>Update</Button>
                            </Group>
                        </Flex>
                    </Box>
                </Box>
            </Modal>
        </Box>
    )
}

export default ProductVariantsSection
